import {ApolloLink} from '@apollo/client';
import {splitQueryBySchema} from 'src/internal/revamp/graphql/compositionFragments/splitQueryBySchema';
import createMutationSchemaLink from 'src/internal/revamp/links/createMutationSchemaLink';
import createSplitLinkObservable from 'src/internal/revamp/links/createSplitLinkObservable';

import type {InternalOperation} from 'src/internal/revamp/apollo/types';
import type {QueryBySchema} from 'src/internal/revamp/graphql/compositionFragments/splitQueryBySchema';
import type {SchemaLinksConfiguration} from 'src/internal/revamp/links/createSchemaLinksConfiguration';
import type {SplitQueryExecution} from 'src/internal/revamp/links/createSplitLinkObservable';

function createExecutions(
  queriesBySchema: QueryBySchema[],
  variables: Record<string, any>,
): SplitQueryExecution[] {
  const executions: SplitQueryExecution[] = [];

  queriesBySchema.forEach((queryBySchema) => {
    const {
      queryDoc,
      schemaLink,
      containsNonNullableFields,
      includeVariableNames,
    } = queryBySchema;

    if (includeVariableNames.length > 0) {
      const isAnyIncludeVariableTrue = includeVariableNames.some(
        (variableName) => variables[variableName],
      );

      if (!isAnyIncludeVariableTrue) {
        return;
      }
    }

    executions.push({
      query: queryDoc,
      link: schemaLink,
      variables,
      containsNonNullableFields,
    });
  });

  return executions;
}
/**
 * Creates a link that splits one requests into multiple - one per each schema.
 * It then executes each subquery through its own Apollo Link and stitches the
 * results back together.
 * @param schemaLinksConfiguration The schema links configuration. It contains the schema ID to Apollo link mapping and the top level field name to schema ID mapping.
 * @returns Apollo link that will handle both single and mixed schema queries.
 */

export default function createSplitBySchemaLink({
  schemaIdToApolloLink,
  topLevelFieldNameToMetadata,
}: SchemaLinksConfiguration) {
  const mutationLink = createMutationSchemaLink(
    schemaIdToApolloLink,
    topLevelFieldNameToMetadata.mutations,
  );

  return new ApolloLink(function compositionQueryTerminatingLink(
    operation: InternalOperation,
    forward,
  ) {
    if (operation.getContext().graphQlOperation?.type === 'MUTATION') {
      // Be aware that when used as a terminating link, forward is undefined
      return mutationLink.request(operation, forward);
    }

    const queriesBySchema = splitQueryBySchema(
      operation.query,
      topLevelFieldNameToMetadata.queries,
      schemaIdToApolloLink,
      operation.operationName,
    );

    const executions: SplitQueryExecution[] = createExecutions(
      queriesBySchema,
      operation.variables,
    );

    return createSplitLinkObservable(executions, operation);
  });
}
